/**
 * 登录/注册入口鉴权逻辑（Phase 1 从 main-login.liquid / main-register-landing.liquid 抽出）
 *
 * 职责：
 * 1. 登录页：尽早将 URL 参数写入 sessionStorage（checkout_url、transfer 等）
 * 2. 已登录用户入口守卫（setupAsyncLoginEntryGuard）
 * 3. 登录页：Google OAuth hash 回调处理
 */
;(function () {
  'use strict';

  /** 将 URL 查询参数持久化到 sessionStorage，供后续登录流程使用 */
  function persistLoginUrlParams() {
    try {
      var params = new URLSearchParams(window.location.search);
      var checkoutUrl = params.get('checkout_url');
      if (checkoutUrl) {
        sessionStorage.setItem('postLoginCheckoutUrl', checkoutUrl);
      }

      var flag = params.get('flag');
      var orderName = params.get('orderName');
      var email = params.get('email');

      if (flag === 'transfer' && orderName && email) {
        sessionStorage.setItem('transferFlag', 'true');
        sessionStorage.setItem('transferOrderName', orderName);
        sessionStorage.setItem('transferEmail', email);
      } else {
        // 清除旧的 transfer 数据，避免污染
        sessionStorage.removeItem('transferFlag');
        sessionStorage.removeItem('transferOrderName');
        sessionStorage.removeItem('transferEmail');
      }
    } catch (e) {
      // 忽略
    }
  }

  /** 登录页：已登录则重定向到账户/transfer 页 */
  function setupLoginPageEntryGuard() {
    if (!window.setupAsyncLoginEntryGuard) return;
    window.setupAsyncLoginEntryGuard({
      includePaths: [/\/pages\/login\/?$/],
      resolveTarget: function () {
        try {
          var params = new URLSearchParams(window.location.search);
          var flag = params.get('flag');
          var orderName = params.get('orderName');
          var email = params.get('email');
          if (flag === 'transfer' && orderName && email) {
            return `/pages/deposit-transfer?orderName=${encodeURIComponent(orderName)}&email=${encodeURIComponent(email)}`;
          }
        } catch (e) {}
        var pointsReturn = window.consumePointsLoginReturn?.();
        if (pointsReturn) return pointsReturn;
        var isMobile = window.innerWidth <= 734;
        var target = isMobile ? '/pages/mobile-menu' : '/pages/my-account';
        return window.withSitePrefix ? window.withSitePrefix(target) : target;
      },
      redirectOnInvalid: false
    });
  }

  /** 登录页：处理 OAuth 授权失败兜底 & Google hash 回调 */
  function handleLoginPageOAuthRedirect() {
    try {
      // 兜底处理：第三方授权取消/失败（重定向返回带 error=）
      if (window.location.hash && window.location.hash.includes('error=')) {
        window.showToast('Sign up failed, please try again.', 'error', 5000);
        try {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (_) {}
      }

      // 解析 Google 回调（登录页场景）
      if (
        window.location.hash &&
        (window.location.hash.includes('id_token=') || window.location.hash.includes('access_token='))
      ) {
        // 使用立即执行的异步函数来处理Google OAuth回调
        (async function () {
          try {
            const hash = new URLSearchParams(window.location.hash.substring(1));
            const idToken = hash.get('id_token');
            const accessToken = hash.get('access_token');

            if (idToken || accessToken) {
              // 使用第三方登录接口：Google => thirdPlatform: 2
              const baseURL = window.API_BASE_URL;
              const response = await fetch(`${baseURL}/authority/api/third/token`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Client-Type': 'app',
                },
                body: JSON.stringify({
                  thirdPlatform: 2,
                  idToken: idToken || '',
                }),
              });

              if (!response.ok) {
                throw new Error(response.message);
              }

              const loginResp = await response.json();
              if (loginResp && loginResp.code === 200) {
                // 先清除所有旧的 token，确保使用新的 token
                if (window.authAPI && window.authAPI.clearAllTokens) {
                  window.authAPI.clearAllTokens();
                }

                // 保存 accessToken / refreshToken
                if (loginResp.data) {
                  if (loginResp.data.accessToken) {
                    window.authAPI.saveAccessToken(loginResp.data.accessToken);
                  }
                  if (loginResp.data.refreshToken) {
                    window.authAPI.saveRefreshToken(loginResp.data.refreshToken);
                  }
                  if (window.userProfileAPI && window.userProfileAPI.clearCachedUserData) {
                    window.userProfileAPI.clearCachedUserData();
                  }
                }

                // 检查是否是注册成功的情况
                if (loginResp.message && loginResp.message.toLowerCase().includes('we created an account for you')) {
                  // 注册成功，仅当有 messageCode 时显示成功消息
                  if (loginResp.messageCode != null) {
                    window.showToast(loginResp.message || 'Operation completed successfully.', 'normal', 3000);
                  }
                  window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/register-success?source=google') : '/pages/register-success?source=google';
                } else if (loginResp && loginResp.messageCode != null) {
                  // 绑定成功，仅当返回了 messageCode 时显示提示
                  window.showToast(loginResp.message || 'Operation completed successfully.', 'normal', 3000);
                }

                const isMobile = window.innerWidth <= 734;

                // Check for transfer flag
                const transferFlag = sessionStorage.getItem('transferFlag');
                const transferOrderName = sessionStorage.getItem('transferOrderName');
                const transferEmail = sessionStorage.getItem('transferEmail');

                if (transferFlag === 'true' && transferOrderName && transferEmail) {
                  window.location.href = `/pages/deposit-transfer?orderName=${encodeURIComponent(transferOrderName)}&email=${encodeURIComponent(transferEmail)}`;
                  return;
                }

                const pointsReturn = window.consumePointsLoginReturn?.();
                if (pointsReturn) { window.location.href = pointsReturn; return; }
                if (isMobile) {
                  window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/mobile-menu') : '/pages/mobile-menu';
                } else {
                  window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/my-account') : '/pages/my-account';
                }
                return;
              }
            }
          } catch (e) {
            console.error('Google login API error:', e);
            // 显示错误信息 - 优先显示后端返回的message
            const errorMessage = e.message || 'Login failed, please try again.';
            window.showToast(errorMessage, 'error', 5000);
          }
        })();
      }
    } catch (e) {
      console.error('Handle Google redirect on login error:', e);
    }
  }

  /** 注册着陆页：已登录则重定向到账户页 */
  function setupRegisterLandingPageEntryGuard() {
    if (!window.setupAsyncLoginEntryGuard) return;
    window.setupAsyncLoginEntryGuard({
      includePaths: [/\/pages\/main-register-landing\/?$/],
      resolveTarget: function () {
        var isMobile = window.innerWidth <= 734;
        var target = isMobile ? '/pages/mobile-menu' : '/pages/my-account';
        return window.withSitePrefix ? window.withSitePrefix(target) : target;
      },
      redirectOnInvalid: false
    });
  }

  /** @deprecated 保留供显式调用；注册页已自动初始化 */
  function initForRegisterLandingPage() {
    setupRegisterLandingPageEntryGuard();
  }

  /** @deprecated 保留供 Phase 2 显式调用；登录页已自动初始化 */
  function initForLoginPage() {
    setupLoginPageEntryGuard();
    handleLoginPageOAuthRedirect();
  }

  var pathname = window.location.pathname;

  // 登录页：尽早执行 URL 参数 → sessionStorage（不等待 DOM）
  if (/\/pages\/login\/?$/.test(pathname)) {
    persistLoginUrlParams();
  }

  // 按当前页面路径初始化守卫与 OAuth 回调
  document.addEventListener('DOMContentLoaded', function () {
    if (/\/pages\/login\/?$/.test(pathname)) {
      setupLoginPageEntryGuard();
      handleLoginPageOAuthRedirect();
    } else if (/\/pages\/main-register-landing\/?$/.test(pathname)) {
      setupRegisterLandingPageEntryGuard();
    }
  });

  window.LoginAuthEntry = {
    persistLoginUrlParams: persistLoginUrlParams,
    setupLoginPageEntryGuard: setupLoginPageEntryGuard,
    handleLoginPageOAuthRedirect: handleLoginPageOAuthRedirect,
    setupRegisterLandingPageEntryGuard: setupRegisterLandingPageEntryGuard,
    initForLoginPage: initForLoginPage,
    initForRegisterLandingPage: initForRegisterLandingPage
  };
})();
